import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, FileDown, Grid3X3, LayoutList, Loader2, MessageCircle, Play, RefreshCw, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { tahkimMerkez } from "../../../lib/tahkimApi";
import TahkimOnTahkimKpiStrip from "./TahkimOnTahkimKpiStrip";
import { AktifKart, BekleyenKart } from "./TahkimOnTahkimDosyaKart";
import { DURUM_LABELS, DURUM_STYLES, SEGMENT_OPTIONS } from "./onTahkimConstants";

function DurumBadge({ durum }) {
    const key = durum || "BEKLIYOR";
    return (
        <span
            className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-semibold uppercase ${
                DURUM_STYLES[key] || DURUM_STYLES.BEKLIYOR
            }`}
        >
            {DURUM_LABELS[key] || key}
        </span>
    );
}

export default function TahkimOnTahkimGelismis({ q = "" }) {
    const [pano, setPano] = useState(null);
    const [segment, setSegment] = useState("tumu");
    const [filterDurum, setFilterDurum] = useState("");
    const [viewMode, setViewMode] = useState("grid");
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(null);
    const [selected, setSelected] = useState(new Set());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { segment };
            if (filterDurum) params.on_tahkim_sure_durumu = filterDurum;
            if (q.trim()) params.q = q.trim();
            const data = await tahkimMerkez.onTahkimPano(params);
            setPano(data);
            setSelected(new Set());
        } catch {
            toast.error("Ön tahkim panosu yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [segment, filterDurum, q]);

    useEffect(() => {
        load();
    }, [load]);

    const bekleyenler = pano?.bekleyenler?.items || [];
    const aktif = pano?.aktif?.items || [];
    const ozet = pano?.ozet;

    const hazirSecilebilir = useMemo(
        () =>
            bekleyenler.filter(
                (r) =>
                    r.hazir ||
                    r.on_tahkim_sure_durumu === "HAZIR" ||
                    r.on_tahkim_sure_durumu === "GECIKTI"
            ),
        [bekleyenler]
    );

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    };

    const selectAllHazir = () => {
        setSelected(new Set(hazirSecilebilir.map((r) => r.sigorta_dosya_id)));
    };

    const uygula = async (row, zorla = false) => {
        setActing(row.sigorta_dosya_id);
        try {
            await tahkimMerkez.onTahkimUygula({
                sigorta_dosya_id: row.sigorta_dosya_id,
                whatsapp: true,
                zorla,
            });
            toast.success(
                zorla
                    ? "Ön tahkime geçildi — WhatsApp kuyruğa alındı"
                    : "Ön tahkime geçildi — müvekkile WhatsApp gönderildi"
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Geçiş uygulanamadı");
        } finally {
            setActing(null);
        }
    };

    const bulkUygula = async () => {
        if (!selected.size) {
            toast.error("Dosya seçin");
            return;
        }
        setBulkBusy(true);
        let ok = 0;
        let fail = 0;
        for (const id of selected) {
            try {
                await tahkimMerkez.onTahkimUygula({
                    sigorta_dosya_id: id,
                    whatsapp: true,
                    zorla: false,
                });
                ok += 1;
            } catch {
                fail += 1;
            }
        }
        setBulkBusy(false);
        if (ok) toast.success(`${ok} dosya ön tahkime geçirildi`);
        if (fail) toast.error(`${fail} dosya geçirilemedi`);
        load();
    };

    const runTara = async () => {
        try {
            const r = await tahkimMerkez.onTahkimTara({});
            toast.success(
                `Tarama: ${r.uygulanan || 0} geçti, ${r.hazir || 0} hazır, ${r.gecikti || 0} gecikmiş`
            );
            load();
        } catch {
            toast.error("Tarama başarısız");
        }
    };

    const runBackfill = async () => {
        try {
            const r = await tahkimMerkez.onTahkimBackfill();
            toast.success(`Gönderim tarihi güncellendi (${r.basvurudan || 0} kayıt)`);
            load();
        } catch {
            toast.error("Backfill başarısız");
        }
    };

    const indirPdf = async (tur) => {
        setPdfBusy(tur);
        try {
            const params = { tur };
            if (q.trim()) params.q = q.trim();
            await tahkimMerkez.exportRaporPdf(params);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(e?.message || "PDF oluşturulamadı");
        } finally {
            setPdfBusy(null);
        }
    };

    const segmentPdfTur =
        segment === "aktif"
            ? "on_tahkim_aktif"
            : segment === "bekleyen"
              ? "on_tahkim_bekleyen"
              : "tam";

    const showBekleyen = segment === "tumu" || segment === "bekleyen";
    const showAktif = segment === "tumu" || segment === "aktif";

    return (
        <div className="space-y-5">
            <section className="rounded-2xl border border-sky-500/25 bg-gradient-to-r from-sky-500/10 via-[#121A2F]/80 to-violet-500/8 p-4 md:p-5">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-sky-200 mb-2">
                            <Sparkles className="h-3.5 w-3.5" />
                            Ön Tahkim Komuta Modülü
                        </div>
                        <h2 className="text-lg md:text-xl font-semibold text-white">
                            Tüm ön tahkim dosyaları — bekleme kuyruğu ve aktif aşama
                        </h2>
                        <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                            Başvuru süresi dolan dosyalar bekleme kuyruğunda listelenir; geçiş sonrası
                            Kanban&apos;daki <strong className="text-sky-300">ON_TAHKIM</strong> kolonunda
                            aktif dosyalarınız burada birleşik görünür.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={load}
                            disabled={loading}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                        </button>
                        <button
                            type="button"
                            onClick={runBackfill}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-white/5"
                        >
                            Gönderim tarihi doldur
                        </button>
                        <button
                            type="button"
                            onClick={runTara}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600/80 hover:bg-violet-600 text-xs text-white font-medium"
                        >
                            <Play className="h-3.5 w-3.5" /> Süre taraması
                        </button>
                        <button
                            type="button"
                            disabled={!!pdfBusy}
                            onClick={() => indirPdf(segmentPdfTur)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#D4AF37]/35 bg-[#D4AF37]/10 text-xs text-[#F3E5AB] hover:bg-[#D4AF37]/20 disabled:opacity-50"
                        >
                            {pdfBusy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <FileDown className="h-3.5 w-3.5" />
                            )}
                            Segment PDF
                        </button>
                        <button
                            type="button"
                            disabled={!!pdfBusy}
                            onClick={() => indirPdf("tam")}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#D4AF37]/85 text-[#0B0F19] text-xs font-semibold disabled:opacity-50"
                        >
                            <FileDown className="h-3.5 w-3.5" /> Tam PDF
                        </button>
                    </div>
                </div>
            </section>

            <TahkimOnTahkimKpiStrip
                ozet={ozet}
                filterDurum={filterDurum}
                segment={segment}
                onFilterDurum={setFilterDurum}
                onSegment={setSegment}
            />

            {ozet?.kurallar?.length > 0 && (
                <div className="rounded-xl border border-white/10 bg-[#0B0F19]/60 px-4 py-3 text-[10px] text-slate-500">
                    <span className="text-slate-400 font-semibold">Süre kuralları: </span>
                    {ozet.kurallar.map((k) => (
                        <span key={k.id} className="mr-3">
                            {k.sigorta_sirketi_adi || "Tümü"}
                            {k.kanal ? ` / ${k.kanal}` : ""} → {k.bekleme_gun} gün
                            {k.aciklama ? ` (${k.aciklama})` : ""}
                        </span>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-1.5 p-1 rounded-xl border border-white/10 bg-[#0B0F19]/80">
                    {SEGMENT_OPTIONS.map((s) => (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => setSegment(s.key)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                                segment === s.key
                                    ? "bg-sky-600/40 text-white border border-sky-500/40"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                            }`}
                            title={s.hint}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                <div className="flex gap-1 p-1 rounded-lg border border-white/10 bg-[#0B0F19]/80">
                    <button
                        type="button"
                        onClick={() => setViewMode("grid")}
                        className={`p-1.5 rounded ${viewMode === "grid" ? "bg-violet-600/40 text-white" : "text-slate-500"}`}
                        title="Kart görünümü"
                    >
                        <Grid3X3 className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode("table")}
                        className={`p-1.5 rounded ${viewMode === "table" ? "bg-violet-600/40 text-white" : "text-slate-500"}`}
                        title="Tablo görünümü"
                    >
                        <LayoutList className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 p-4 rounded-xl border border-emerald-500/35 bg-emerald-500/8">
                    <span className="text-sm text-emerald-200 font-semibold">
                        {selected.size} dosya seçili
                    </span>
                    <button
                        type="button"
                        onClick={bulkUygula}
                        disabled={bulkBusy}
                        className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
                    >
                        {bulkBusy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Send className="h-3.5 w-3.5" />
                        )}
                        Toplu ön tahkime geç
                    </button>
                    <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className="text-xs text-slate-400 hover:text-white"
                    >
                        Seçimi temizle
                    </button>
                </div>
            )}

            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 text-xs text-cyan-100/90 flex gap-2">
                <MessageCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                    <strong>WhatsApp otomasyonu:</strong> Geçişte müvekkile eksper raporu talimatı
                    kurumsal mesaj olarak iletilir. Resmi STK tahkim başvurusu Kanban&apos;da sizin
                    onayınızla ilerler.
                </div>
            </div>

            {showAktif && (
                <section className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-sky-200 flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
                            Aktif ön tahkim dosyaları
                            <span className="text-slate-500 font-normal">({aktif.length})</span>
                        </h3>
                    </div>

                    {aktif.length === 0 && !loading ? (
                        <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
                            Henüz ON_TAHKIM aşamasında aktif dosya yok
                        </div>
                    ) : viewMode === "grid" ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                            {aktif.map((row) => (
                                <AktifKart key={row.tahkim_id} row={row} />
                            ))}
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-white/10">
                            <table className="w-full text-xs">
                                <thead className="bg-[#121A2F] text-slate-500 uppercase">
                                    <tr>
                                        <th className="p-2 text-left">Dosya</th>
                                        <th className="p-2 text-left">Müvekkil</th>
                                        <th className="p-2 text-left">Şirket</th>
                                        <th className="p-2 text-left">Hakem</th>
                                        <th className="p-2 text-left">Geçiş</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aktif.map((row) => (
                                        <tr
                                            key={row.tahkim_id}
                                            className="border-t border-white/5 hover:bg-sky-500/5"
                                        >
                                            <td className="p-2">
                                                <Link
                                                    to={`/e/tahkim-dosyalari/${row.tahkim_id}`}
                                                    className="text-[#F3E5AB] font-mono hover:underline"
                                                >
                                                    {row.dosya_no || row.tahkim_id}
                                                </Link>
                                            </td>
                                            <td className="p-2 text-slate-300">{row.muvekkil_ad}</td>
                                            <td className="p-2 text-slate-400">{row.sigorta_sirketi}</td>
                                            <td className="p-2 text-slate-400">{row.hakem || "—"}</td>
                                            <td className="p-2 font-mono text-slate-500">
                                                {row.asama_guncelleme?.slice(0, 10) || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {showBekleyen && (
                <section className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                            Bekleme kuyruğu
                            <span className="text-slate-500 font-normal">({bekleyenler.length})</span>
                        </h3>
                        {hazirSecilebilir.length > 0 && (
                            <button
                                type="button"
                                onClick={selectAllHazir}
                                className="text-[10px] text-violet-300 hover:text-violet-200"
                            >
                                Hazır olanların tümünü seç ({hazirSecilebilir.length})
                            </button>
                        )}
                    </div>

                    {bekleyenler.length === 0 && !loading ? (
                        <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
                            Bu filtrede bekleyen dosya yok
                        </div>
                    ) : viewMode === "grid" ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                            {bekleyenler.map((row) => (
                                <BekleyenKart
                                    key={row.sigorta_dosya_id}
                                    row={row}
                                    acting={acting === row.sigorta_dosya_id}
                                    onUygula={uygula}
                                    selected={selected.has(row.sigorta_dosya_id)}
                                    onToggleSelect={() => toggleSelect(row.sigorta_dosya_id)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-white/10">
                            <table className="w-full text-xs">
                                <thead className="bg-[#121A2F] text-slate-500 uppercase">
                                    <tr>
                                        <th className="p-2 w-8" />
                                        <th className="p-2 text-left">Dosya</th>
                                        <th className="p-2 text-left">Müvekkil</th>
                                        <th className="p-2 text-left">Hedef</th>
                                        <th className="p-2 text-left">Durum</th>
                                        <th className="p-2 text-right">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bekleyenler.map((row) => {
                                        const kapali =
                                            row.dosya_durumu === "KAPATILDI" ||
                                            row.dosya_durumu === "KAPATILDI";
                                        const canSelect =
                                            !kapali &&
                                            (row.hazir ||
                                                row.on_tahkim_sure_durumu === "HAZIR" ||
                                                row.on_tahkim_sure_durumu === "GECIKTI");
                                        return (
                                            <tr
                                                key={row.sigorta_dosya_id}
                                                className="border-t border-white/5 hover:bg-white/5"
                                            >
                                                <td className="p-2">
                                                    {canSelect && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.has(row.sigorta_dosya_id)}
                                                            onChange={() =>
                                                                toggleSelect(row.sigorta_dosya_id)
                                                            }
                                                            className="rounded"
                                                        />
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <Link
                                                        to={`/e/sigorta-dosyalari/${row.sigorta_dosya_id}`}
                                                        className="text-[#F3E5AB] font-mono hover:underline"
                                                    >
                                                        {row.dosya_no || row.sigorta_dosya_id}
                                                    </Link>
                                                </td>
                                                <td className="p-2 text-slate-300">{row.muvekkil_ad}</td>
                                                <td className="p-2">
                                                    <span className="font-mono text-slate-300">
                                                        {row.on_tahkim_hedef_tarihi || "—"}
                                                    </span>
                                                    {row.gun_gecikme > 0 && (
                                                        <div className="text-[10px] text-red-400 flex items-center gap-0.5">
                                                            <AlertTriangle className="h-3 w-3" />
                                                            {row.gun_gecikme} gün geçti
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <DurumBadge durum={row.on_tahkim_sure_durumu} />
                                                </td>
                                                <td className="p-2 text-right">
                                                    {canSelect && (
                                                        <button
                                                            type="button"
                                                            disabled={acting === row.sigorta_dosya_id}
                                                            onClick={() => uygula(row, false)}
                                                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/70 text-white text-[10px] disabled:opacity-50"
                                                        >
                                                            {acting === row.sigorta_dosya_id ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <Send className="h-3 w-3" />
                                                            )}
                                                            Geç
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {loading && (
                <div className="flex justify-center py-8">
                    <Loader2 className="h-7 w-7 animate-spin text-sky-400" />
                </div>
            )}

            {!loading && hazirSecilebilir.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-emerald-300/80">
                    <CheckCircle2 className="h-4 w-4" />
                    {hazirSecilebilir.length} dosya geçişe hazır — tek tek veya toplu işlem
                    yapabilirsiniz.
                </div>
            )}
        </div>
    );
}
